// product-service.ts
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, combineLatest, of } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';
import { TextService } from './text-service';
import { SignupAPIService } from './signup-apiservice';
import {
  buildOffice365Catalog,
  Office365LayoutConfig,
} from '../utils/o365-product-mapper';
import { O365ProductApiResponse } from '../models/o365-product.models';

export interface CatalogItem {
  offeringId: string;
  productId: string;
  quantity: string | number;
  price: number;
  type: 'product' | 'addon';
  name_label?: string;
  description_label?: string;
  limit_label?: string;
  price_label?: string;
  quantity_label?: string;
  name?: string;
  description?: string;
  limit?: string;
  price_title?: string;
  quantity_title?: string;
  icon?: string;
  group?: string; // rackspace / o365 / o365-addon
  offerId?: string;
  externalOfferId?: string;
  serviceCode?: number;
  xmlAttributeName?: string;
  billingCycle?: 'monthly' | 'annual';
  allowedProducts?: string[];
  parentProductId?: string;
  addons?: CatalogItem[];
  /** Annual-commitment variant of the same product (from API, matched by externalOfferId). */
  annualVariant?: CatalogItem;
}

export interface Office365Tab {
  name: string;
  products: CatalogItem[];
}

export interface Office365Catalog {
  tabs: Office365Tab[];
  addons: CatalogItem[];
}

export interface ProductCatalog {
  apps: {
    products: CatalogItem[];
    general: {
      addon_title: string;
      per_mailbox_per_month: string;
    };
  };
  domain: Record<string, number>;
  office365: Office365Catalog;
}

interface ProductCatalogConfig {
  apps: ProductCatalog['apps'];
  domain: Record<string, number>;
  office365: Office365LayoutConfig;
}

@Injectable({ providedIn: 'root' })
export class ProductService {
  public productData$: Observable<ProductCatalog | null>;

  constructor(
    private http: HttpClient,
    private textService: TextService,
    private signupApi: SignupAPIService
  ) {
    this.productData$ = combineLatest([
      this.http.get<ProductCatalogConfig>('/assets/data/product.json'),
      this.signupApi.getO365Products().pipe(
        catchError(() => of({ total: 0, o365Products: [] } as O365ProductApiResponse))
      ),
      this.textService.textData$,
    ]).pipe(
      map(([catalog, o365ApiResponse, _]) => {
        const office365 = buildOffice365Catalog(o365ApiResponse, catalog.office365);
        const mergedCatalog = { ...catalog, office365 };
        const enriched = this.textService.injectDescriptionsIntoProducts(mergedCatalog);

        enriched.apps.products.forEach((p: CatalogItem) => {
          if (!p.group) p.group = p.type === 'addon' ? 'addon' : 'rackspace';
        });

        enriched.office365.tabs.forEach((tab: Office365Tab) => {
          tab.products.forEach((p: CatalogItem) => {
            if (!p.group) p.group = 'o365';
            (p.addons ?? []).forEach((addon: CatalogItem) => {
              if (!addon.group) addon.group = 'o365-addon';
            });
            if (p.annualVariant) {
              if (!p.annualVariant.group) p.annualVariant.group = 'o365';
              (p.annualVariant.addons ?? []).forEach((addon: CatalogItem) => {
                if (!addon.group) addon.group = 'o365-addon';
              });
            }
          });
        });

        return enriched;
      }),
      shareReplay(1)
    );
  }

  /** All rackspace products */
  get rackspaceProducts$(): Observable<CatalogItem[]> {
    return this.productData$.pipe(
      map((d) => d?.apps?.products.filter((p) => p.group === 'rackspace') ?? [])
    );
  }

  /** All Rackspace Addons */
  get rackspaceAddons$(): Observable<CatalogItem[]> {
    return this.productData$.pipe(
      map((d) => d?.apps?.products.filter((p) => p.group === 'addon') ?? [])
    );
  }

  /** Office 365 products */
  get office365Products$(): Observable<CatalogItem[]> {
    return this.productData$.pipe(
      map(
        (d) =>
          d?.office365?.tabs.flatMap((tab) => tab.products.filter((p) => p.group === 'o365')) ?? []
      )
    );
  }

  /** Office 365 addons (flattened from each product's addons array) */
  get office365Addons$(): Observable<CatalogItem[]> {
    return this.productData$.pipe(
      map(
        (d) =>
          d?.office365?.tabs.flatMap((tab) =>
            tab.products.flatMap((p) => p.addons ?? [])
          ) ?? []
      )
    );
  }

  /** Domain pricing list */
  get domainPricing$(): Observable<Record<string, number>> {
    return this.productData$.pipe(map((d) => d?.domain ?? {}));
  }

  /** Office 365 tabs */
  get office365Tabs$(): Observable<Office365Tab[]> {
    return this.productData$.pipe(map((d) => d?.office365?.tabs ?? []));
  }
}
